import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { withStorageSync } from '@angular-architects/ngrx-toolkit';
import { initialStatus, LoadingStatus } from '@bcef/ia-shared';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { debounceTime, pipe, switchMap, tap } from 'rxjs';

import { ErrorStore } from '@shared/store/error.store';

import { RfvslmnpRawForm } from '../interfaces/rfvslmnp-raw-form.interface';
import { RfvslmnpRequest } from '../interfaces/rfvslmnp-request.interface';
import { RfvslmnpResponse } from '../interfaces/rfvslmnp-response.interface';
import { RfvslmnpService } from '../services/rfvslmnp.service';

export interface RfvslmnpState {
  rfvslmnpForm: RfvslmnpRawForm;
  rfvslmnpRequest: Partial<RfvslmnpRequest>;
  rfvslmnpResponse: RfvslmnpResponse;
  loadingStatus: LoadingStatus;
}

export const initialState: RfvslmnpState = {
  rfvslmnpForm: {
    project: { simulationType: null, projectType: null },
    details: {
      price: null,
      agencyFee: null,
      acquisitionFeeChoice: null,
      acquisitionFeeAmount: null,
      furniturePrice: null,
      acquisitionDate: null,
    },
    costs: {
      monthlyRent: null,
      annualRent: null,
      monthlyCosts: null,
      annualCosts: null,
      revaluation: null,
    },
    construction: {
      firstYear: null,
      secondYear: null,
      firstYearImprovements: null,
      secondYearImprovements: null,
    },
    funding: {
      hasMortgage: null,
      mortgageType: null,
      mortgageAmount: null,
      interestRate: null,
      mortgageTerm: null,
      years: null,
      months: null,
      date: null,
    },
    distribution: { land: null, averageDepreciationRate: null },
    tax: {
      estimationMode: null,
      tmiRate: null,
      familySituation: null,
      childrenCount: null,
      netTaxableIncome: null,
    },
  },
  rfvslmnpRequest: {},
  rfvslmnpResponse: {
    annualData: [],
    synthesisData: {
      rf: { cashFlowBeforeTaxes: 0, tax: 0, netCashFlow: 0 },
      lmnp: { cashFlowBeforeTaxes: 0, tax: 0, netCashFlow: 0 },
    },
    suggestionCodes: [],
  },
  loadingStatus: initialStatus,
};
export const RFVSLMNP_FORM_DEBOUNCE_MILLIS = 300;

export const RfvslmnpStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withStorageSync({ key: 'ia-simulateur-epargne:rfvslmnp', storage: () => sessionStorage }),
  withMethods(
    (
      store,
      rfvslmnpService = inject(RfvslmnpService),
      errorStore = inject(ErrorStore),
      router = inject(Router),
    ) => ({
      sendRfvslmnpForm: rxMethod<void>(
        pipe(
          tap(() =>
            patchState(store, (state) => ({
              loadingStatus: { ...state.loadingStatus, isLoading: true },
            })),
          ),
          debounceTime(RFVSLMNP_FORM_DEBOUNCE_MILLIS),
          switchMap(() =>
            rfvslmnpService.postRfvslmnpSimulation(store.rfvslmnpRequest()).pipe(
              tapResponse({
                next: (rfvslmnpResponse) => {
                  patchState(store, (state) => ({
                    rfvslmnpResponse,
                    loadingStatus: { ...state.loadingStatus, isLoaded: true },
                  }));
                  router.navigate(['rfvslmnp', 'summary']);
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.setError(error);
                  patchState(store, (state) => ({
                    loadingStatus: { ...state.loadingStatus, error },
                  }));
                  router.navigate(['erreur']);
                },
                finalize: () =>
                  patchState(store, (state) => ({
                    loadingStatus: { ...state.loadingStatus, isLoading: false },
                  })),
              }),
            ),
          ),
        ),
      ),

      addRawFormData(formGroupData: RfvslmnpRawForm): void {
        patchState(store, { rfvslmnpForm: formGroupData });
      },

      addRequestData(request: Partial<RfvslmnpRequest>): void {
        patchState(store, { rfvslmnpRequest: request });
      },
    }),
  ),
);

export type IRfvslmnpStore = InstanceType<typeof RfvslmnpStore>;
